import { CopyToClipboard } from 'react-copy-to-clipboard';
import { currentOrigin, generalBlue, hthConfigOptions } from 'src/app-config';
import { FaCopy } from 'react-icons/fa';
import { FC, Fragment, useMemo, useState } from 'react';
import { generalHelpers, generalHthHelper } from '@monorepo/helpers';
import { hideLoadingModalReducer, setAlertResponse, showLoadingModalReducer } from 'src/store';
import { houseToHouseServices, hthHelper } from 'src/services';
import { QrCode, WhatsAppIcon1 } from 'src/components/_commons';
import { typeHTHTerritory, typeHTHFace, typeTerritoryNumber } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetConfig, useGetIsMobile } from 'src/custom-hooks';
import { WhatsappShareButton } from 'react-share';

type propsType = {
    hthTerritory: typeHTHTerritory;
    territoryNumber: typeTerritoryNumber;
};

export const HTHShareAllBuildingsButtons: FC<propsType> = ({ hthTerritory, territoryNumber }) => {
    const [copiedAllLinksToClipboard, setCopiedAllLinksToClipboard] = useState(false);
    const [copiedUniqueLinkToClipboard, setCopiedUniqueLinkToClipboard] = useState(false);
    const [showQrCode, setShowQrCode] = useState(false);
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isMobile = useGetIsMobile();

    const shareAllLinksText: string = useMemo(() => {
        const text = hthHelper.GetLinkTextForShareAllBuildings(currentOrigin, hthTerritory, config.isUsingLettersForBlocks);
        return text;
    }, [config, hthTerritory.faces, hthTerritory.mapUrl, territoryNumber]);

    const shareUniqueLinkText: string = useMemo(() => {
        const text = generalHelpers.GetLinkTextForHthUniqueAccess(currentOrigin, config.congregation, config.name, territoryNumber);
        return text;
    }, [config, currentOrigin, territoryNumber]);

    const shareHandler = async (isUniqueLink: boolean) => {
        if (!hthTerritory.faces.some(f => f.buildings?.length)) {
            return;
        }
        if (!generalHelpers.IsToday(hthTerritory.dateOfLastSharing ?? 1)) {
            dispatch(showLoadingModalReducer());
            const response = await houseToHouseServices.SetIsSharedBuildings(territoryNumber);
            dispatch(hideLoadingModalReducer());
            if (!response.success) {
                dispatch(setAlertResponse({ response }));
                return;
            }
        }
        if (isUniqueLink) {
            setCopiedUniqueLinkToClipboard(true);
        } else {
            setCopiedAllLinksToClipboard(true);
        }
    }

    return (
        <>
        <style>
            {`
                .share-btn {
                    border-radius: 10px;
                    border: 1px solid lightgrey;
                    color: inherit;
                    height: 72px;
                    margin-inline: 10px;
                    text-align: left;
                    text-decoration: none;
                    transition: color 0.15s ease, border-color 0.15s ease;
                    width: 300px;
                }
                .share-btn:hover, .share-btn:focus, .share-btn:active {
                    border-color: #0070f3;
                    color: #0070f3;
                }
            `}
        </style>
        {showQrCode &&
            <QrCode
                closeModal={() => setShowQrCode(false)}
                title={`COMPARTIR TERRITORIO ${territoryNumber}`}
                url={generalHelpers.GetLinkForHthUniqueAccess(currentOrigin, config.congregation, territoryNumber)}
            />
        }
        <div className={'row mt-3'}>
            <div className={'text-center'}>
                <CopyToClipboard text={shareAllLinksText}>
                    <button className={`btn ${copiedAllLinksToClipboard ? 'btn-general-red' : 'btn-general-blue'} py-2 my-2 ${isMobile ? '' : 'me-2'}`}
                        style={{ width: '300px', maxWidth: '95%', height: '72px' }}
                        onClick={() => shareHandler(false)}
                        disabled={!shareAllLinksText}
                    >
                        {copiedAllLinksToClipboard ? 
                            "Copiados!"
                            :
                            <div className={'row'}>
                                <div className={'col-2'}>
                                    <FaCopy size={'30px'} className='mt-1 ms-2' />
                                </div>
                                <div className={'col-10 px-1'}>
                                    Copiar Edificios de todo el Territorio para compartir
                                </div>
                            </div>
                        }
                    </button>
                </CopyToClipboard>
                <CopyToClipboard text={shareUniqueLinkText}>
                    <button className={`btn ${copiedUniqueLinkToClipboard ? 'btn-general-red' : 'btn-general-blue'} py-2 my-2 ${isMobile ? '' : 'ms-2'}`}
                        style={{ width: '300px', maxWidth: '95%', height: '72px' }}
                        onClick={() => {
                            copiedUniqueLinkToClipboard ? setShowQrCode(true) : shareHandler(true);
                        }}
                        disabled={!shareUniqueLinkText}
                    >
                        {copiedUniqueLinkToClipboard ? 
                            "Copiado! Otro click para compartir por QR"
                            :
                            <div className={'row'}>
                                <div className={'col-2'}>
                                    <FaCopy size={'30px'} className='mt-1 ms-2' />
                                </div>
                                <div className={'col-10 px-1'}>
                                    Copiar link único de todo el Territorio para compartir
                                </div>
                            </div>
                        }
                    </button>
                </CopyToClipboard>
            </div>
        </div>
        <div className={`${isMobile ? 'mb-5' : 'mb-1'}`}>
            {hthConfigOptions.blocks.map(b =>
                <Fragment key={b}>
                    {!!hthTerritory.faces.filter(p => p.block === b && p.buildings?.length).length &&
                        <ShareBlock
                            faces={hthTerritory.faces.filter(p => p.block === b)}
                            territoryNumber={territoryNumber}
                        />
                    }
                </Fragment>
            )}
        </div>
        </>
    );
}

type propsType1 = {
    faces: typeHTHFace[];
    territoryNumber: typeTerritoryNumber;
};

const ShareBlock: FC<propsType1> = ({ faces, territoryNumber }) => {
    const [copiedToClipboard1, setCopiedToClipboard1] = useState(false);
    const [copiedToClipboard2, setCopiedToClipboard2] = useState(false);
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isMobile = useGetIsMobile();

    const shareLinks = useMemo(() => {
        const text = hthHelper.GetLinkTextForShareBlockBuildings(currentOrigin, config.congregation, territoryNumber, faces, config.isUsingLettersForBlocks);
        return text;
    }, [config, currentOrigin, faces, territoryNumber]);

    const shareLink = useMemo(() => {
        const text = hthHelper.GetLinkTextForShareBlockBuildingsUnique(currentOrigin, config.congregation, config.name, territoryNumber, faces, config.isUsingLettersForBlocks);
        return text;
    }, [config, currentOrigin, faces, territoryNumber]);

    const shareHandler = async (isUniqueLink: boolean): Promise<void> => {
        if (!generalHelpers.IsToday(faces[0].dateOfLastSharing ?? 1)) {
            dispatch(showLoadingModalReducer());
            const response = await houseToHouseServices.SetIsSharedBuildings(territoryNumber, faces[0].block);
            dispatch(hideLoadingModalReducer());
            if (!response.success) {
                dispatch(setAlertResponse({ response }));
                return;
            }
        }
        if (isUniqueLink) {
            setCopiedToClipboard2(true);
        } else {
            setCopiedToClipboard1(true);
        }
    }

    return (
        <div className={'row'}>
            <div className={'col-md-6'}>
                <div className={isMobile ? 'mt-2' : 'd-flex justify-content-end'}>
                    <div style={{ marginBlock: isMobile ? '0' : '10px' }}>
                        <div className={'row d-flex align-items-center justify-content-center'}>
                            <div className={`card share-btn ${copiedToClipboard1 ? 'btn-general-red' : 'btn-light'} text-center pointer pt-1`} onClick={() => {}}>
                                <CopyToClipboard text={shareLinks}>
                                    <WhatsappShareButton
                                        onClick={() => {}}
                                        url={shareLinks}
                                        style={{ marginTop: '7px' }}
                                        windowWidth={660}
                                        windowHeight={460}
                                    >
                                        {copiedToClipboard1 ?
                                            <div className={'mt-2'}>
                                                Copiados! Manzana {generalHthHelper.MaskTheBlock(faces[0].block, config.isUsingLettersForBlocks)}
                                            </div>
                                            :
                                            <div className={'row'} onClick={() => shareHandler(false)}>
                                                <div className={'col-2'}>
                                                    <WhatsAppIcon1 styles={{ width: '45px' }} />
                                                </div>
                                                <div className={'col-10 px-1'}>
                                                    &nbsp; Manzana {generalHthHelper.MaskTheBlock(faces[0].block, config.isUsingLettersForBlocks)}: Enviar los Edificios por WhatsApp
                                                    (hay {faces.map(f => f.buildings).filter(b => b && b.length > 0).flat().length})
                                                </div>
                                            </div>
                                        }
                                    </WhatsappShareButton>
                                </CopyToClipboard>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'col-md-6'}>
                <div className={isMobile ? 'mt-2' : 'd-flex justify-content-start'}>
                    <div style={{ marginBlock: isMobile ? '0' : '10px' }}>
                        <div className={'row d-flex align-items-center justify-content-center'}>
                            <CopyToClipboard text={shareLink}>
                                <div className={`card share-btn ${copiedToClipboard2 ? 'btn-general-red' : 'btn-light'} text-center pointer pt-1`}
                                    onClick={() => shareHandler(true)}
                                >
                                    {copiedToClipboard2 ?
                                        <div className={'mt-3'}>
                                            Copiado! Manzana {generalHthHelper.MaskTheBlock(faces[0].block, config.isUsingLettersForBlocks)}
                                        </div>
                                        :
                                        <div className={'row'} style={{ marginTop: '7px' }}>
                                            <div className={'col-2'}>
                                                <FaCopy size={'30px'} className='mt-1 ms-2' color={generalBlue} />
                                            </div>
                                            <div className={'col-10 px-1'}>
                                                &nbsp; Manzana {generalHthHelper.MaskTheBlock(faces[0].block, config.isUsingLettersForBlocks)}: Copiar link único para compartir
                                                (hay {faces.map(f => f.buildings).filter(b => b && b.length > 0).flat().length})
                                            </div>
                                        </div>
                                    }
                                </div>
                            </CopyToClipboard>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
